import React, { useEffect, useRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from "wavesurfer.js/plugins/regions";
import {ApplicationStore} from "../../../../store/ApplicationStore";


interface WaveformDisplayProps {
    peaks: Float32Array | null;
    duration: number; // in seconds
    rangeEnabled: boolean;
}

const WaveformDisplay: React.FC<WaveformDisplayProps> = ({ peaks, duration, rangeEnabled }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const regionsPluginRef = useRef<ReturnType<typeof RegionsPlugin.create> | null>(null);
    const activeRegionRef = useRef<any>(null);

    useEffect(() => {
        if (!containerRef.current || !peaks) return;

        // Clean up existing instance
        wavesurferRef.current?.destroy();

        // @ts-ignore
        const ws = WaveSurfer.create({
            container: containerRef.current,
            waveColor: '#1a1a1a',
            progressColor: '#1a1a1a',
            cursorWidth: 0,
            interact: rangeEnabled,
            height: "auto",
            normalize: false,
            barWidth: 0.5,
            barGap: 0.5,
            barRadius: 0,
            duration: duration,
            peaks: [peaks],
            barHeight: 0.8,
            mediaControls: false,
        });

        ws.on('init', () => {
            wavesurferRef.current = ws;
            regionsPluginRef.current = ws.registerPlugin(RegionsPlugin.create());

            if (rangeEnabled) {
                regionsPluginRef.current.enableDragSelection({
                    color: 'rgba(0, 0, 255, 0.1)',
                    id: "draggable",
                });

                regionsPluginRef.current.on('region-created', (region: any) => {
                    // Clear any existing regions before creating a new one
                    try {
                        if (activeRegionRef.current && activeRegionRef.current !== region) {
                            activeRegionRef.current.remove();
                        }
                    } catch (e) {
                        console.log("Error calling remove on active region")
                    }
                    activeRegionRef.current = region;
                    ApplicationStore.setSelectedRange({ start: region.start, end: region.end });
                });

                regionsPluginRef.current.on('region-updated', (region: any) => {
                    ApplicationStore.setSelectedRange({ start: region.start, end: region.end });
                });
            }
        });

        ws.load('',
            [peaks],   // ✅ passed to load()
            duration)

        wavesurferRef.current = ws;

        return () => {
            ApplicationStore.setSelectedRange(null);
            ws.destroy();
        };
    }, [peaks, duration, rangeEnabled]);

    useEffect(() => {
        const plugin = regionsPluginRef.current;
        const ws = wavesurferRef.current;
        if (!plugin || !ws) return;

        ws.setOptions({ interact: rangeEnabled });

        if (rangeEnabled) {
            plugin.enableDragSelection({ color: 'rgba(0,0,0,0.3)' });
        } else {
            try {
                if (activeRegionRef.current) {
                    activeRegionRef.current.remove();
                    activeRegionRef.current = null;
                }
            } catch (e) {
                console.log("Exception removing active region")
            }
            ApplicationStore.setSelectedRange(null);
        }
    }, [rangeEnabled]);

    // Click-away handler
    useEffect(() => {
        if (!rangeEnabled) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                // Check if the click is on the context menu
                const contextMenu = document.querySelector('.context-menu__content');
                if (contextMenu && contextMenu.contains(event.target as Node)) {
                    return; // Don't clear selection if clicking on context menu
                }
                
                // Clicked outside the waveform and not on context menu
                try {
                    if (activeRegionRef.current) {
                        activeRegionRef.current.remove();
                        activeRegionRef.current = null;
                    }
                } catch (e) {
                    console.log("Exception removing active region")
                }

                ApplicationStore.setSelectedRange(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [rangeEnabled]);

    return (
        <div
            ref={containerRef}
            style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, pointerEvents: rangeEnabled ? 'auto' : 'none' }}
        />
    );
};

export default WaveformDisplay;
